import React, { useEffect, useState } from 'react';
import { portalMonthly } from '../api';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from 'recharts';

const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const fmt = (n: number) => '€' + n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

export default function Reports() {
  const [monthly, setMonthly] = useState<any[]>([]);

  useEffect(() => {
    portalMonthly().then(r => setMonthly(r.data.months || []));
  }, []);

  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;

  const chartData = Array.from({ length: 12 }, (_, i) => {
    let month = currentMonth - 11 + i;
    let year = currentYear;
    if (month <= 0) { month += 12; year -= 1; }
    const thisYear = monthly.find(m => m.year === year && m.month === month);
    const lastYear = monthly.find(m => m.year === year - 1 && m.month === month);
    return {
      label: MONTH_NAMES[month - 1] + ' \'' + String(year).slice(2),
      thisYear: thisYear?.gross || 0,
      lastYear: lastYear?.gross || 0,
      count: thisYear?.count || 0,
    };
  });

  const totalThisYear = chartData.reduce((s, d) => s + d.thisYear, 0);
  const totalLastYear = chartData.reduce((s, d) => s + d.lastYear, 0);
  const totalCount = chartData.reduce((s, d) => s + d.count, 0);

  return (
    <div>
      <h2 style={{ fontSize: 18, fontWeight: 500, marginBottom: 24 }}>Reports</h2>

      {/* KPI */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 24 }}>
        {[
          { label: 'Rolling 12 Months Revenue', value: fmt(totalThisYear) },
          { label: 'Previous Year Revenue', value: fmt(totalLastYear) },
          { label: 'Rolling 12 Months Receipts', value: totalCount },
        ].map((s, i) => (
          <div key={i} style={{ background: 'white', border: '0.5px solid #e0e0e0', borderRadius: 8, padding: '20px' }}>
            <div style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>{s.label}</div>
            <div style={{ fontSize: 26, fontWeight: 600, color: '#185FA5' }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Chart */}
      <div style={{ background: 'white', border: '0.5px solid #e0e0e0', borderRadius: 8, padding: 24, marginBottom: 24 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#555', marginBottom: 16 }}>Monthly Revenue — This Year vs Last Year</div>
        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} tickFormatter={v => '€' + v} />
            <Tooltip formatter={(v: any) => fmt(Number(v))} />
            <Legend />
            <Bar dataKey="thisYear" name="This Year" fill="#185FA5" radius={[3,3,0,0]} />
            <Line dataKey="lastYear" name="Last Year" stroke="#94B8D9" strokeWidth={2} dot={{ r: 3 }} type="monotone" />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Monthly Table */}
      <div style={{ background: 'white', border: '0.5px solid #e0e0e0', borderRadius: 8, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f8f9fa', borderBottom: '0.5px solid #e0e0e0' }}>
              <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 500, color: '#555' }}>Month</th>
              <th style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 500, color: '#555' }}>Receipts</th>
              <th style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 500, color: '#555' }}>Revenue</th>
              <th style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 500, color: '#555' }}>vs Last Year</th>
            </tr>
          </thead>
          <tbody>
            {[...chartData].reverse().map((d, i) => {
              const diff = d.thisYear - d.lastYear;
              const pct = d.lastYear > 0 ? ((diff / d.lastYear) * 100).toFixed(1) : null;
              return (
                <tr key={i} style={{ borderBottom: '0.5px solid #f0f0f0', background: i % 2 === 0 ? 'white' : '#fafafa' }}>
                  <td style={{ padding: '10px 14px' }}>{d.label}</td>
                  <td style={{ padding: '10px 14px', textAlign: 'right', color: '#666' }}>{d.count}</td>
                  <td style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 500 }}>{fmt(d.thisYear)}</td>
                  <td style={{ padding: '10px 14px', textAlign: 'right', color: diff >= 0 ? '#1D9E75' : '#E24B4A', fontSize: 12 }}>
                    {pct ? (diff >= 0 ? '+' : '') + pct + '%' : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
